import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import type {
  ChangeCatalogItem,
  ChangeExtraFields,
  ChangeExtraValue,
  ChangeFormField,
  ChangeWorkGroup,
} from '../../../types/changes';
import {
  extraFieldVisibleFromNatives,
  weekdayConstraintHint,
} from '../changeFormSchema';
import { CHANGE_FIELD_LIMITS } from '../changeValidation';
import {
  ASSIGNMENT_PESSOA,
  ASSIGNMENT_SETOR,
  peopleFromAreas,
  type SubtaskAssignmentKind,
} from '../changeSubtaskAssignment';
import ChangeExtraFieldInput from './ChangeExtraFieldInput';

export interface SubtaskCreateFormValues {
  title: string;
  assignmentKind: SubtaskAssignmentKind;
  groupId: string;
  assigneeId: string;
  dueDate: string;
  comments: string;
  taskTypeId: string;
}

interface Props {
  fields: ChangeFormField[];
  form: SubtaskCreateFormValues;
  extras: ChangeExtraFields;
  onFormChange: (patch: Partial<SubtaskCreateFormValues>) => void;
  onExtraChange: (fieldId: number, value: ChangeExtraValue) => void;
  areas: ChangeWorkGroup[];
  taskTypes: ChangeCatalogItem[];
  windowStart: string;
  windowEnd: string;
  windowHelp: string;
}

const colProps = (span: number) => (span === 6 ? { xs: 12 as const, md: 6 as const } : { xs: 12 as const });

export function subtaskNativeMap(form: SubtaskCreateFormValues): Record<string, string> {
  return {
    title: form.title,
    destination: form.assignmentKind,
    assignment_kind: form.assignmentKind,
    group_id: form.groupId,
    assignee_id: form.assigneeId,
    due_date: form.dueDate,
    comments: form.comments,
    task_type_id: form.taskTypeId,
  };
}

const ChangeSubtaskConfigurableForm: React.FC<Props> = ({
  fields,
  form,
  extras,
  onFormChange,
  onExtraChange,
  areas,
  taskTypes,
  windowStart,
  windowEnd,
  windowHelp,
}) => {
  const natives = subtaskNativeMap(form);

  const renderNative = (field: ChangeFormField) => {
    const key = field.native_key;
    const label = field.label;
    if (key === 'title') {
      return (
        <Form.Control
          aria-label={label}
          maxLength={CHANGE_FIELD_LIMITS.title}
          value={form.title}
          onChange={(e) => onFormChange({ title: e.target.value })}
        />
      );
    }
    if (key === 'task_type_id') {
      return (
        <Form.Select
          aria-label={label}
          value={form.taskTypeId}
          onChange={(e) => onFormChange({ taskTypeId: e.target.value })}
        >
          <option value="">Selecione o tipo da tarefa</option>
          {taskTypes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Form.Select>
      );
    }
    if (key === 'destination') {
      return (
        <>
          <div className="d-flex flex-wrap gap-3 mb-2">
            <Form.Check
              type="radio"
              id="subtask-dest-setor"
              name="subtask-assignment"
              label="Grupo"
              checked={form.assignmentKind === ASSIGNMENT_SETOR}
              onChange={() => onFormChange({ assignmentKind: ASSIGNMENT_SETOR, assigneeId: '' })}
            />
            <Form.Check
              type="radio"
              id="subtask-dest-pessoa"
              name="subtask-assignment"
              label="Pessoa específica"
              checked={form.assignmentKind === ASSIGNMENT_PESSOA}
              onChange={() => onFormChange({ assignmentKind: ASSIGNMENT_PESSOA, groupId: '' })}
            />
          </div>
          <Form.Label>Grupo</Form.Label>
          <Form.Select
            value={form.groupId}
            aria-label="Grupo que vai receber a subtarefa"
            disabled={form.assignmentKind === ASSIGNMENT_PESSOA}
            onChange={(e) => onFormChange({ groupId: e.target.value, assigneeId: '' })}
          >
            <option value="">
              {form.assignmentKind === ASSIGNMENT_PESSOA
                ? 'Não se aplica'
                : 'Selecione o grupo que vai receber'}
            </option>
            {areas.map((area) => (
              <option key={area.group_id} value={area.group_id}>
                {area.name}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            {form.assignmentKind === ASSIGNMENT_PESSOA
              ? 'O grupo é definido automaticamente pela pessoa escolhida.'
              : 'Membros deste grupo assumem e respondem.'}
          </Form.Text>
          {form.assignmentKind === ASSIGNMENT_PESSOA ? (
            <div className="mt-2">
              <Form.Label>Pessoa</Form.Label>
              <Form.Select
                value={form.assigneeId}
                aria-label="Pessoa que vai receber a subtarefa"
                onChange={(e) => onFormChange({ assigneeId: e.target.value })}
              >
                <option value="">Selecione a pessoa</option>
                {peopleFromAreas(areas).map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.name || `Usuário #${member.user_id}`}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">Somente esta pessoa poderá assumir.</Form.Text>
            </div>
          ) : null}
        </>
      );
    }
    if (key === 'due_date') {
      return (
        <>
          <Form.Control
            type="date"
            aria-label={label}
            min={windowStart}
            max={windowEnd}
            value={form.dueDate}
            onChange={(e) => onFormChange({ dueDate: e.target.value })}
          />
          <Form.Text className="text-muted">{windowHelp}</Form.Text>
        </>
      );
    }
    if (key === 'comments') {
      return (
        <Form.Control
          as="textarea"
          rows={2}
          aria-label={label}
          value={form.comments}
          onChange={(e) => onFormChange({ comments: e.target.value })}
        />
      );
    }
    return null;
  };

  let lastSection = '';
  return (
    <Row className="g-2">
      {fields.map((field) => {
        if (field.kind === 'native' && !field.is_visible && !field.is_locked) return null;
        if (field.kind === 'extra' && !extraFieldVisibleFromNatives(field, fields, natives, extras)) {
          return null;
        }
        const section = field.section_name || '';
        const showSection = Boolean(section && section !== lastSection);
        if (section) lastSection = section;
        return (
          <React.Fragment key={field.id}>
            {showSection ? (
              <Col xs={12}>
                <h6 className="mb-0">{section}</h6>
              </Col>
            ) : null}
            <Col {...colProps(field.col_span)}>
              {field.native_key === 'destination' ? (
                <Form.Label>Enviar para</Form.Label>
              ) : (
                <Form.Label>{field.label}</Form.Label>
              )}
              {field.kind === 'native' ? renderNative(field) : (
                <ChangeExtraFieldInput
                  field={field}
                  value={extras[String(field.id)]}
                  onChange={(value) => onExtraChange(field.id, value)}
                />
              )}
              {field.help_text && field.native_key !== 'due_date' ? (
                <Form.Text className="text-muted">{field.help_text}</Form.Text>
              ) : null}
              {weekdayConstraintHint(field.weekday_constraint) ? (
                <Form.Text className="text-muted d-block">
                  {weekdayConstraintHint(field.weekday_constraint)}
                </Form.Text>
              ) : null}
            </Col>
          </React.Fragment>
        );
      })}
    </Row>
  );
};

export default ChangeSubtaskConfigurableForm;
